import React, { useState } from 'react';
import { Scale, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

interface SourceEntry {
    title: string;
    legislationReference?: string;
    /** Optional override for the manual label prefix (default: "HMRC Manual") */
    hmrcManualLabel?: string;
    hmrcManualReference?: string;
}

interface RegulationReferenceProps {
    /** Single-source mode */
    title?: string;
    legislationReference?: string;
    hmrcManualReference?: string;
    externalLink?: string;
    /** Multi-source mode */
    sources?: SourceEntry[];
    /** Optional context line shown above the sources */
    contextLine?: string;
    /** Override the button label (default: "Sources & HMRC guidance") */
    label?: string;
    /**
     * ISO date of death. When set, a "Guidance as at <date>" line is shown,
     * making explicit that the references are those in force at the date of death.
     */
    asAtDate?: string | null;
}

/** Format an ISO date (YYYY-MM-DD) as "6 April 2024" for display. */
function formatUkDate(iso?: string | null): string | null {
    if (!iso || iso.length < 10) return null;
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export const RegulationReference: React.FC<RegulationReferenceProps> = ({
    title,
    legislationReference,
    hmrcManualReference,
    externalLink,
    sources,
    contextLine,
    label,
    asAtDate,
}) => {
    const [open, setOpen] = useState(false);
    const asAt = formatUkDate(asAtDate);

    // Build a unified source list — multi-source takes priority, single-source as fallback
    const entries: SourceEntry[] = sources && sources.length > 0
        ? sources
        : title
            ? [{ title, legislationReference, hmrcManualReference }]
            : [];

    if (entries.length === 0) return null;

    const buttonLabel = label ?? 'Sources & HMRC Guidance';

    return (
        <div className="mt-2">
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1 text-[11px] text-[#3730A3] hover:text-[#2E2780] font-medium transition-colors"
            >
                <Scale className="w-3 h-3" />
                <span>{buttonLabel}</span>
                {open
                    ? <ChevronUp className="w-3 h-3" />
                    : <ChevronDown className="w-3 h-3" />}
            </button>

            {open && (
                <div className="mt-1.5 rounded bg-[#FAFAF8] border border-[#E8E4DB] p-2.5 animate-in slide-in-from-top-1 duration-200">
                    {contextLine && (
                        <p className="text-[11px] text-[#6B6762] leading-relaxed mb-2">
                            {contextLine}
                        </p>
                    )}

                    <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1.5">
                        Sources
                    </p>

                    {asAt && (
                        <p className="text-[10px] text-[#6B6762] mb-1.5">
                            Guidance as at the date of death (<span className="tabular-nums">{asAt}</span>)
                        </p>
                    )}

                    <div className="space-y-2">
                        {entries.map((entry, i) => (
                            <div key={i} className={i > 0 ? 'pt-2 border-t border-[#E8E4DB]' : ''}>
                                <p className="text-[11px] font-medium text-[#1A1A1A]">{entry.title}</p>
                                {entry.legislationReference && (
                                    <p className="text-[11px] text-[#3730A3] font-mono tabular-nums">{entry.legislationReference}</p>
                                )}
                                {entry.hmrcManualReference && (
                                    <p className="text-[11px] text-[#6B6762]">{entry.hmrcManualLabel ?? 'HMRC Manual'}: <span className="font-mono tabular-nums">{entry.hmrcManualReference}</span></p>
                                )}
                            </div>
                        ))}
                    </div>

                    {externalLink && (
                        <a
                            href={externalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-[#3730A3] hover:text-[#2E2780] hover:underline mt-2"
                        >
                            View on HMRC <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                    )}

                    <div className="mt-2 pt-2 border-t border-[#E8E4DB]">
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762]">Legal note</p>
                        <p className="text-[11px] text-[#AFA99F] italic leading-relaxed">
                            These references are provided for informational context only and do not constitute legal advice.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
